import { DataProvider, fetchUtils } from 'react-admin';
import { getJWT } from './auth/tokenUtils';

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8787';

const httpClient = (url: string, options: any = {}) => {
    if (!options.headers) {
        options.headers = new Headers({ Accept: 'application/json' });
    }
    const token = getJWT();
    options.headers.set('Authorization', `Bearer ${token}`);
    return fetchUtils.fetchJson(url, options);
};

export const dataProvider: DataProvider = {
    getList: (resource, params) => {
        const { page = 1, perPage = 10 } = params.pagination || {};
        const { field = 'id', order = 'ASC' } = params.sort || {};
        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
            filter: JSON.stringify(params.filter),
        };
        const url = `${apiUrl}/${resource}?${new URLSearchParams(query)}`;

        return httpClient(url).then(({ headers, json }) => ({
            data: json,
            total: parseInt(headers.get('content-range')?.split('/').pop() || '0', 10),
        }));
    },

    getOne: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`).then(({ json }) => ({
            data: json,
        })),

    getMany: (resource, params) => {
        const query = {
            filter: JSON.stringify({ id: params.ids }),
        };
        const url = `${apiUrl}/${resource}?${new URLSearchParams(query)}`;
        return httpClient(url).then(({ json }) => ({ data: json }));
    },

    getManyReference: (resource, params) => {
        const { page = 1, perPage = 10 } = params.pagination || {};
        const { field = 'id', order = 'ASC' } = params.sort || {};
        const query = {
            sort: JSON.stringify([field, order]),
            range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
            filter: JSON.stringify({
                ...params.filter,
                [params.target]: params.id,
            }),
        };
        const url = `${apiUrl}/${resource}?${new URLSearchParams(query)}`;

        return httpClient(url).then(({ headers, json }) => ({
            data: json,
            total: parseInt(headers.get('content-range')?.split('/').pop() || '0', 10),
        }));
    },

    update: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'PUT',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json })),

    updateMany: (resource, params) => {
        const query = {
            filter: JSON.stringify({ id: params.ids }),
        };
        return httpClient(`${apiUrl}/${resource}?${new URLSearchParams(query)}`, {
            method: 'PUT',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({ data: json }));
    },

    create: (resource, params) =>
        httpClient(`${apiUrl}/${resource}`, {
            method: 'POST',
            body: JSON.stringify(params.data),
        }).then(({ json }) => ({
            data: json,
        })),

    delete: (resource, params) =>
        httpClient(`${apiUrl}/${resource}/${params.id}`, {
            method: 'DELETE',
        }).then(({ json }) => ({ data: json })),

    deleteMany: (resource, params) => {
        const query = {
            filter: JSON.stringify({ id: params.ids }),
        };
        return httpClient(`${apiUrl}/${resource}?${new URLSearchParams(query)}`, {
            method: 'DELETE',
        }).then(({ json }) => ({ data: json }));
    },
};

// ---------------------------------------------------------------------------
// Shared types used by custom methods
// ---------------------------------------------------------------------------

export interface Employee {
    employee_id: string;
    full_name: string;
    email: string;
    employee_type?: 'internal' | 'external' | 'system';
    /** Department the employee belongs to, sourced from Azure AD. */
    department?: string | null;
    /**
     * Employment window bounds (YYYY-MM-DD). Only populated on
     * `GET /timesheets` responses, so {@link TimesheetGrid} can disable grid
     * cells that fall outside the employee's employment window.
     */
    join_date?: string | null;
    /** @see join_date */
    termination_date?: string | null;
}

/**
 * A recipient in the "Notify Others" list.
 * Known employees carry a string `employee_id`; external recipients
 * entered by e-mail address have `employee_id: null`.
 */
export interface NotifyRecipient {
    employee_id: string | null;
    full_name: string;
    email: string;
}

export interface TimeoffRequest {
    id: number;
    employee_id?: string;
    leave_type: 'Annual' | 'Unpaid';
    start_date: string;
    end_date: string;
    start_partial: 'Full' | 'FirstHalf' | 'SecondHalf';
    end_partial: 'Full' | 'FirstHalf' | 'SecondHalf';
    work_days: number;
    status: 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';
    note?: string;
    reviewed_by?: string;
    reviewed_date?: string;
    review_comment?: string;
    created_date: string;
    /** Employee full name, populated when fetching approval requests */
    full_name?: string;
    /** Employee email, populated when fetching approval requests */
    email?: string;
}

export interface LeaveBalance {
    leave_type: 'Annual' | 'Unpaid';
    allocated_days: number;
    used_days: number;
}

export interface CompanyLeaveBalance {
    employee_id: string;
    full_name: string;
    email: string;
    leave_type: 'Annual' | 'Unpaid';
    allocated_days: number;
    used_days: number;
}

// ---------------------------------------------------------------------------
// Generic authenticated HTTP helper
// ---------------------------------------------------------------------------

/**
 * Makes an authenticated HTTP request to the API.
 *
 * Prepends `apiUrl` to `path` and injects the JWT `Authorization` header
 * automatically via the shared `httpClient` wrapper.
 *
 * @param path    - Path relative to `apiUrl` (e.g. `'timeoff/requests'`).
 * @param options - Optional fetch options (method, body, headers, …).
 * @returns The raw `fetchUtils.fetchJson` response `{ status, headers, body, json }`.
 */
export const customRequest = (
    path: string,
    options: Record<string, unknown> = {}
): Promise<{ status: number; headers: Headers; body: string; json: unknown }> =>
    httpClient(`${apiUrl}/${path}`, options);

// ---------------------------------------------------------------------------
// Named custom methods for ad-hoc endpoints
// ---------------------------------------------------------------------------

/**
 * Fetches the next working day from the backend.
 *
 * @returns An object `{ date: string }` with the ISO-format date string.
 */
export const getNextWorkday = (employeeId?: string): Promise<{ date: string }> => {
    const query = employeeId ? `?employee_id=${encodeURIComponent(employeeId)}` : '';
    return customRequest(`timeoff/next-workday${query}`).then(({ json }) => json as { date: string });
};

/**
 * Calculates the number of working days in a date range.
 *
 * @param params - `start`, `end` dates and `start_partial` / `end_partial` selectors.
 * @returns An object `{ work_days: number }`.
 */
export const getWorkdays = (params: {
    start: string;
    end: string;
    start_partial: string;
    end_partial: string;
    employee_id?: string;
}): Promise<{ work_days: number }> =>
    customRequest(`timeoff/workdays?${new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<string, string>
    )}`).then(
        ({ json }) => json as { work_days: number }
    );

/**
 * Creates a new time-off request.
 *
 * @param payload - The request body for the new time-off request.
 * @returns The created request object returned by the API.
 */
export const createTimeoffRequest = (
    payload: Record<string, unknown>
): Promise<unknown> =>
    customRequest('timeoff/requests', {
        method: 'POST',
        body: JSON.stringify(payload),
    }).then(({ json }) => json);

/**
 * Retrieves the current user's time-off requests.
 *
 * @returns Array of `TimeoffRequest` objects.
 */
export const getTimeoffRequests = (): Promise<TimeoffRequest[]> =>
    customRequest('timeoff/requests').then(({ json }) => json as TimeoffRequest[]);

/**
 * Cancels a pending time-off request.
 *
 * @param id - The numeric ID of the request to cancel.
 * @returns The updated request object returned by the API.
 */
export const cancelTimeoffRequest = (id: number): Promise<unknown> =>
    customRequest(`timeoff/requests/${id}/cancel`, { method: 'PUT' }).then(
        ({ json }) => json
    );

/**
 * Retrieves all time-off requests that are pending approval by the current user.
 *
 * @returns Array of `TimeoffRequest` objects awaiting approval.
 */
export const getTimeoffApprovals = (): Promise<TimeoffRequest[]> =>
    customRequest('timeoff/requests?for_approval=true').then(
        ({ json }) => json as TimeoffRequest[]
    );

/**
 * Approves or rejects a time-off request.
 *
 * @param id            - The numeric ID of the request.
 * @param action        - Either `'approve'` or `'reject'`.
 * @param reviewComment - Optional comment when rejecting.
 * @returns The updated request object returned by the API.
 */
export const approveOrRejectRequest = (
    id: number,
    action: 'approve' | 'reject',
    reviewComment?: string
): Promise<unknown> =>
    customRequest(`timeoff/requests/${id}/${action}`, {
        method: 'PUT',
        body: action === 'reject' ? JSON.stringify({ review_comment: reviewComment }) : undefined,
    }).then(({ json }) => json);

/**
 * Retrieves the leave balances for a given employee (or the current user if
 * `employeeId` is omitted or `null`).
 *
 * @param employeeId - Optional employee ID to fetch balances on behalf of.
 * @returns Array of `LeaveBalance` objects.
 */
export const getLeaveBalances = (employeeId?: string | null): Promise<LeaveBalance[]> => {
    const path = employeeId
        ? `timeoff/balances?employee_id=${encodeURIComponent(employeeId)}`
        : 'timeoff/balances';
    return customRequest(path).then(({ json }) => json as LeaveBalance[]);
};

/**
 * Retrieves leave balances for all employees (company-wide view).
 *
 * Calls `GET /timeoff/balances?scope=company` and returns an array of
 * balance records, each including the employee's name and email alongside
 * the leave-type breakdown.
 *
 * @returns Array of `CompanyLeaveBalance` objects.
 */
export const getAllLeaveBalances = (): Promise<CompanyLeaveBalance[]> =>
    customRequest('timeoff/balances?scope=company').then(
        ({ json }) => json as CompanyLeaveBalance[]
    );

/**
 * Searches for user suggestions matching a query string.
 *
 * @param query - The search term (at least 2 characters recommended).
 * @returns Array of matching `Employee` objects.
 */
export const getEmployeeSuggestions = (query: string): Promise<Employee[]> =>
    customRequest(`users/suggestions?q=${encodeURIComponent(query)}`).then(
        ({ json }) => json as Employee[]
    );

/**
 * Retrieves the current authenticated user's profile, including
 * their `department` field as sourced from Azure AD.
 *
 * Calls `GET /users/me` which returns the user record for the
 * authenticated user. Returns `null` if the request fails (e.g. if the
 * user is not yet in the users table).
 *
 * @returns The current user's `Employee` record, or `null` on failure.
 */
export const getCurrentUserInfo = (): Promise<Employee | null> =>
    customRequest('users/me')
        .then(({ json }) => json as Employee)
        .catch(() => null);

/**
 * Checks whether the authenticated user has a given permission scope.
 *
 * Calls `GET /check-permission?scope=<scope>` and returns `true` when the
 * backend confirms the user is permitted.
 *
 * @param scope - The permission scope to check (e.g. `'timeoff.approve'`).
 * @returns `true` if the user has the permission, `false` otherwise.
 */
export const checkPermission = (scope: string): Promise<boolean> =>
    customRequest(`check-permission?scope=${encodeURIComponent(scope)}`)
        .then(({ json }) => (json as { permitted: boolean }).permitted)
        .catch(() => false);

/** A single row in the leave-balances CSV */
export interface LeaveBalanceCsvRow {
    full_name: string;
    email: string;
    leave_type: string;
    balance: number | string;
    leave_units_type?: string;
    /**
     * When present and valid (non-negative number), sets `allocated_days`
     * directly, bypassing the normal balance-based calculation on import.
     */
    allocated?: number | string;
}

/**
 * Triggers a download of all active employees' current-year leave balances as
 * a CSV file (`leave_balances.csv`).
 *
 * Makes a `GET /timeoff/balances/export` request and instructs the browser to
 * download the response body.
 */
export const exportLeaveBalances = async (): Promise<void> => {
    const token = getJWT();
    const url = `${apiUrl}/timeoff/balances/export`;
    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Export failed');
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = 'leave_balances.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
};

/**
 * Imports leave balances by sending parsed CSV rows and an import mode to the
 * backend.
 *
 * @param rows - Array of parsed CSV row objects.
 * @param mode - `'override'` replaces existing balances; `'add'` increments them.
 * @returns The API response `{ updated, created, warnings }`.
 */
export const importLeaveBalances = (
    rows: LeaveBalanceCsvRow[],
    mode: 'override' | 'add'
): Promise<{ updated: number; created: number; warnings: string[] }> =>
    customRequest('timeoff/balances/import', {
        method: 'POST',
        body: JSON.stringify({ rows, mode }),
    }).then(({ json }) => json as { updated: number; created: number; warnings: string[] });

/** A single row in the leave-requests import CSV. */
export interface ImportedRequest {
    name: string;
    email: string;
    leave_type: string;
    start_date: string;
    end_date: string;
    comments?: string;
    number_of_paid_days?: number | string;
    optional_leave_days?: number | string;
    number_of_unpaid_days?: number | string;
    leave_units_type?: string;
}

/** A row skipped on import because a matching request already exists. */
export interface ImportRequestDuplicate {
    email: string;
    leave_type: string;
    start_date: string;
    end_date: string;
}

/** Balance-change preview for a single row, returned when `dryRun: true`. */
export interface ImportRequestBalanceDiff {
    employee_id: string;
    name: string;
    email: string;
    leave_type: string;
    year: number;
    currentUsedDays: number;
    newUsedDays: number;
    currentBalance: number;
    newBalance: number;
}

/** Response shape from `POST /timeoff/requests/import`. */
export interface ImportRequestsResult {
    imported: number;
    duplicates: ImportRequestDuplicate[];
    warnings: string[];
    /** Only present when the request was made with `dryRun: true`. */
    balanceDiffs?: ImportRequestBalanceDiff[];
}

/**
 * Imports leave requests by sending parsed CSV rows to the backend.
 *
 * @param rows - Array of parsed CSV row objects.
 * @param dryRun - When `true`, the backend computes and returns the outcome (including
 *   `balanceDiffs`) without writing to the database, for a confirmation UI.
 * @returns The API response `{ imported, duplicates, warnings, balanceDiffs? }`.
 */
export const importTimeoffRequests = (
    rows: ImportedRequest[],
    dryRun = false
): Promise<ImportRequestsResult> =>
    customRequest('timeoff/requests/import', {
        method: 'POST',
        body: JSON.stringify({ rows, dryRun }),
    }).then(({ json }) => json as ImportRequestsResult);

/**
 * Retrieves all company-wide time-off requests (admin / scope=all view).
 *
 * Calls `GET /timeoff/requests?scope=all` and returns an array of
 * {@link TimeoffRequest} objects, each with `full_name` and `email` populated.
 *
 * @returns Array of `TimeoffRequest` objects.
 */
export const getAllTimeoffRequests = (): Promise<TimeoffRequest[]> =>
    customRequest('timeoff/requests?scope=all').then(
        ({ json }) => json as TimeoffRequest[]
    );

/**
 * Retrieves a single time-off request by its ID.
 *
 * Calls `GET /timeoff/requests/:id`. The caller must hold the `timeoff.read`
 * permission and be either the request owner, the request's manager, or hold
 * the `timeoff.view_all_requests` permission. The response includes
 * `full_name` and `email` from the `employees` table.
 *
 * @param id - The numeric ID of the time-off request.
 * @returns The matching {@link TimeoffRequest} object.
 */
export const getTimeoffRequestById = (id: number): Promise<TimeoffRequest> =>
    customRequest(`timeoff/requests/${id}`).then(({ json }) => json as TimeoffRequest);

// ---------------------------------------------------------------------------
// Overtime
// ---------------------------------------------------------------------------

/**
 * An overtime history entry returned by the API.
 */
export interface OvertimeHistoryEntry {
    id: number;
    declaration_id: number;
    action: 'Submitted' | 'Approved' | 'Cancelled' | 'Updated';
    actor_id: string;
    created_date?: string;
    actor_name?: string;
}

/**
 * An overtime declaration returned by the API.
 */
export interface OvertimeRequest {
    id: number;
    employee_id: string;
    date: string;
    /** Start time in HH:MM format (Vietnam time, UTC+7). */
    start_time: string;
    duration_hours: number;
    duration_minutes: 0 | 15 | 30 | 45;
    task_description: string;
    status: 'Approved' | 'Cancelled';
    notify_others?: string | null;
    manager_id?: string | null;
    created_by: string;
    created_date: string;
    updated_date?: string;
    full_name?: string;
    email?: string;
    history?: OvertimeHistoryEntry[];
}

/**
 * Creates a new overtime declaration.
 *
 * @param payload - The request body for the new overtime declaration.
 * @returns The created declaration object returned by the API.
 */
export const createOvertimeRequest = (
    payload: Record<string, unknown>
): Promise<OvertimeRequest> =>
    customRequest('overtime/requests', {
        method: 'POST',
        body: JSON.stringify(payload),
    }).then(({ json }) => json as OvertimeRequest);

/**
 * Retrieves the current user's overtime declarations.
 *
 * @returns Array of `OvertimeRequest` objects, each with inline history.
 */
export const getOvertimeRequests = (): Promise<OvertimeRequest[]> =>
    customRequest('overtime/requests').then(({ json }) => json as OvertimeRequest[]);

/**
 * Updates an existing overtime declaration.
 *
 * @param id      - The numeric ID of the declaration to update.
 * @param payload - The fields to update.
 * @returns The updated declaration object returned by the API.
 */
export const updateOvertimeRequest = (
    id: number,
    payload: Record<string, unknown>
): Promise<OvertimeRequest> =>
    customRequest(`overtime/requests/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
    }).then(({ json }) => json as OvertimeRequest);

/**
 * Cancels (soft-deletes) an overtime declaration.
 *
 * @param id - The numeric ID of the declaration to cancel.
 * @returns The cancelled declaration object returned by the API.
 */
export const deleteOvertimeRequest = (
    id: number
): Promise<OvertimeRequest> =>
    customRequest(`overtime/requests/${id}`, { method: 'DELETE' }).then(
        ({ json }) => json as OvertimeRequest
    );

/**
 * Filter parameters for the admin overtime list endpoint.
 */
export interface AdminOvertimeFilters {
    /** Filter by employee full name (substring match). */
    name?: string;
    /** Inclusive start date (YYYY-MM-DD). */
    date_from?: string;
    /** Inclusive end date (YYYY-MM-DD). */
    date_to?: string;
    /** Filter by specific employee ID. */
    employee_id?: string;
}

/**
 * Retrieves overtime declarations across all employees (admin view).
 *
 * Calls `GET /overtime/requests?scope=all` with optional filters for name,
 * date range, and employee ID. Requires `overtime.view_all` permission.
 *
 * @param filters - Optional filters to narrow the result set.
 * @returns Array of `OvertimeRequest` objects, each with `full_name`, `email`, and `history`.
 */
export const getAdminOvertimeRequests = (
    filters: AdminOvertimeFilters = {}
): Promise<OvertimeRequest[]> => {
    const params = new URLSearchParams({ scope: 'all' });
    if (filters.name) params.set('name', filters.name);
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.employee_id) params.set('employee_id', filters.employee_id);
    return customRequest(`overtime/requests?${params.toString()}`).then(
        ({ json }) => json as OvertimeRequest[]
    );
};

// ---------------------------------------------------------------------------
// Org Chart
// ---------------------------------------------------------------------------

/**
 * A single node in the organization chart tree.
 */
export interface OrgChartNode {
    employee_id: string;
    full_name: string;
    email: string;
    role: string;
    children: OrgChartNode[];
}

/**
 * Fetches the internal user organization chart from the backend.
 *
 * Calls `GET /users/org-chart` and returns an array of root-level
 * {@link OrgChartNode} objects, each containing nested `children` arrays.
 *
 * @returns Array of root {@link OrgChartNode} objects forming the hierarchy.
 */
export const getOrgChart = (): Promise<OrgChartNode[]> =>
    customRequest('users/org-chart').then(({ json }) => json as OrgChartNode[]);

/**
 * Checks whether the current authenticated user has at least one active
 * subordinate (i.e., an active employee whose `manager_id` equals the
 * current user's `employee_id`).
 *
 * Calls `GET /timeoff/has-subordinates` and returns the boolean result.
 *
 * @returns `true` if the user manages at least one active employee, `false` otherwise.
 */
export const getHasSubordinates = (): Promise<boolean> =>
    customRequest('timeoff/has-subordinates')
        .then(({ json }) => (json as { has_subordinates: boolean }).has_subordinates)
        .catch(() => false);

/**
 * Adjusts a single employee's leave balance.
 *
 * @param params - `employee_id`, `leave_type`, mandatory `reason`, and either:
 *   - `days`: delta (positive to add, negative to deduct), or
 *   - `allocated`: sets `allocated_days` directly (non-negative number), or both.
 * @returns The updated balance info returned by the API.
 */
export const adjustLeaveBalance = (params: {
    employee_id: string;
    leave_type: string;
    days?: number;
    allocated?: number;
    reason: string;
}): Promise<{ employee_id: string; leave_type: string; new_allocated: number; new_balance: number }> =>
    customRequest('timeoff/balances/adjust', {
        method: 'POST',
        body: JSON.stringify(params),
    }).then(
        ({ json }) =>
            json as { employee_id: string; leave_type: string; new_allocated: number; new_balance: number }
    );

// ---------------------------------------------------------------------------
// Night Shift Declarations
// ---------------------------------------------------------------------------

/**
 * A single night shift declaration record.
 */
export interface NightShiftDeclarationRecord {
    id: number;
    employee_id: string;
    shift_date: string;
    status: 'Submitted' | 'Approved' | 'Cancelled';
    created_by: string;
    created_date: string;
    updated_date: string;
    full_name?: string;
    email?: string;
}

/**
 * A history entry for a night shift declaration.
 */
export interface NightShiftDeclarationHistoryRecord {
    id: number;
    declaration_id: number;
    status: string;
    changed_by: string;
    changed_date: string;
    note?: string;
    changed_by_name?: string;
}

/**
 * Retrieves the current user's night shift declarations.
 *
 * Calls `GET /night-shift-declarations` with a large `range` parameter to
 * override the backend's default limit of 10 rows. Without this override,
 * `parseRaQueryParams` defaults to `range=[0,9]`, silently truncating the
 * result set and causing the calendar to display only the first 10 records.
 *
 * The range `[0,9999]` is intentionally large so that all declarations for
 * any realistic employee are returned in a single request, ensuring the
 * calendar view renders every record across all months.
 *
 * @returns Array of {@link NightShiftDeclarationRecord} objects.
 */
export const getNightShiftDeclarations = (): Promise<NightShiftDeclarationRecord[]> =>
    customRequest(`night-shift-declarations?${new URLSearchParams({ range: '[0,9999]' })}`).then(
        ({ json }) => json as NightShiftDeclarationRecord[]
    );

/**
 * Creates night shift declarations for the given dates.
 *
 * Calls `POST /night-shift-declarations` with the optional employee ID and dates.
 * When `employee_id` is omitted, the backend defaults to the authenticated user.
 *
 * @param payload - Object with optional `employee_id` and required `shift_dates` array.
 * @returns The created declaration records.
 */
export const createNightShiftDeclaration = (
    payload: { employee_id?: string; shift_dates: string[] }
): Promise<NightShiftDeclarationRecord[]> =>
    customRequest('night-shift-declarations', {
        method: 'POST',
        body: JSON.stringify(payload),
    }).then(({ json }) => json as NightShiftDeclarationRecord[]);

/**
 * Cancels (soft-deletes) a night shift declaration.
 *
 * Calls `DELETE /night-shift-declarations/:id`.
 *
 * @param id - The numeric ID of the declaration to cancel.
 * @returns The response object with the deleted ID.
 */
export const deleteNightShiftDeclaration = (id: number): Promise<{ id: number }> =>
    customRequest(`night-shift-declarations/${id}`, {
        method: 'DELETE',
    }).then(({ json }) => json as { id: number });

/**
 * Retrieves the history log for a single night shift declaration.
 *
 * Calls `GET /night-shift-declarations/:id/history`.
 *
 * @param id - The numeric ID of the declaration.
 * @returns Array of {@link NightShiftDeclarationHistoryRecord} objects.
 */
export const getNightShiftDeclarationHistory = (
    id: number
): Promise<NightShiftDeclarationHistoryRecord[]> =>
    customRequest(`night-shift-declarations/${id}/history`).then(
        ({ json }) => json as NightShiftDeclarationHistoryRecord[]
    );

// ---------------------------------------------------------------------------
// Holidays
// ---------------------------------------------------------------------------

/**
 * A single public holiday entry returned by the `GET /holidays` endpoint.
 */
export interface HolidayEntry {
    /** ISO 8601 date string (YYYY-MM-DD). */
    date: string;
    /** Human-readable name of the holiday (e.g. "New Year's Day"). */
    holiday_name: string;
    /** Day of the week the holiday falls on (1 = Monday, 7 = Sunday). */
    day_of_week: number;
    /** Whether an HR admin has overridden this date as not-a-holiday. */
    is_overridden: boolean;
    /** The reason given for the override, or `null` when not overridden. */
    override_reason: string | null;
}

// ---------------------------------------------------------------------------
// Employee Accounting Import
// ---------------------------------------------------------------------------

/**
 * A single row parsed from the HR Contact XLSX "Employee" sheet (columns B–V).
 */
export interface AccountingImportRow {
    department?: string;
    full_name?: string;
    english_name?: string;
    gender?: string;
    email?: string;
    address?: string;
    id_number?: string;
    id_issue_date?: string;
    id_issue_place?: string;
    title?: string;
    title_en?: string;
    contract_type?: string;
    dob?: string;
    phone?: string;
    join_date?: string;
    official_contract_date?: string;
    level?: string;
    note?: string;
}

/**
 * Imports employee accounting data by sending parsed XLSX rows to the backend.
 *
 * @param rows - Array of parsed row objects from the "Employee" sheet (cols B–V).
 * @returns The API response `{ imported, skipped, warnings }`.
 */
export const importEmployeeAccounting = (
    rows: AccountingImportRow[]
): Promise<{ imported: number; skipped: number; warnings: string[] }> =>
    customRequest('employees/import-accounting', {
        method: 'POST',
        body: JSON.stringify({ rows }),
    }).then(({ json }) => json as { imported: number; skipped: number; warnings: string[] });

/**
 * Retrieves the list of public holidays for a given year from the backend.
 *
 * Calls `GET /holidays?year=<year>` and returns an array of
 * {@link HolidayEntry} objects sorted by date ascending.
 * Requires a valid JWT (standard authenticated-user access).
 *
 * @param year - The 4-digit year to fetch holidays for (e.g. `2025`).
 * @returns Array of {@link HolidayEntry} objects.
 *
 * @example
 * const holidays = await getHolidays(2025);
 * // [{ date: "2025-01-01", holiday_name: "New Year's Day", day_of_week: 3 }, ...]
 */
export const getHolidays = (year: number): Promise<HolidayEntry[]> =>
    customRequest(`holidays?year=${encodeURIComponent(year)}`).then(
        ({ json }) => json as HolidayEntry[]
    );

/**
 * Marks a public holiday date as not-a-holiday.
 *
 * Calls `POST /holidays/:date/override` with a mandatory `reason`. Requires
 * the `holidays.manage` permission.
 *
 * @param date - The `YYYY-MM-DD` date to override.
 * @param reason - Why the date is not actually a holiday.
 * @returns The updated {@link HolidayEntry}.
 */
export const overrideHoliday = (date: string, reason: string): Promise<HolidayEntry> =>
    customRequest(`holidays/${encodeURIComponent(date)}/override`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
    }).then(({ json }) => json as HolidayEntry);

/**
 * Resets a previous holiday override, restoring the date as a public holiday.
 *
 * Calls `DELETE /holidays/:date/override`. Requires the `holidays.manage`
 * permission.
 *
 * @param date - The `YYYY-MM-DD` date whose override should be reset.
 * @returns The restored {@link HolidayEntry}.
 */
export const resetHolidayOverride = (date: string): Promise<HolidayEntry> =>
    customRequest(`holidays/${encodeURIComponent(date)}/override`, {
        method: 'DELETE',
    }).then(({ json }) => json as HolidayEntry);

// ---------------------------------------------------------------------------
// Employee Document Storage
// ---------------------------------------------------------------------------

/**
 * The twenty-three fixed document-slot identifiers.
 * Must match the `DocType` union in the backend `types.d.ts`.
 */
export type DocType =
    | 'id_card'
    | 'health_insurance_book'
    | 'health_insurance_card'
    | 'vneid_record'
    | 'cv_certified'
    | 'resignation_letter'
    | 'resignation_settlement'
    | 'asset_return'
    | 'post_employment_confidentiality'
    | 'payment_policy_agreement'
    | 'probation_contract'
    | 'asset_handover'
    | 'dependent_proof_1'
    | 'dependent_proof_2'
    | 'dependent_proof_3'
    | 'dependent_proof_4'
    | 'dependent_proof_5'
    | 'dependent_proof_6'
    | 'dependent_proof_7'
    | 'dependent_proof_8'
    | 'dependent_proof_9'
    | 'dependent_proof_10'
    | 'termination_decision';

/**
 * Metadata for a single stored employee document, as returned by the backend.
 */
export interface EmployeeDocumentMeta {
    id: number;
    employee_id: string;
    doc_type: DocType;
    r2_key: string;
    original_filename: string;
    mime_type: string;
    size_bytes: number;
    uploaded_by: string;
    uploaded_at: string;
}

/** Response body of `GET /employees/:id/documents`. */
export type EmployeeDocumentsMap = Record<DocType, EmployeeDocumentMeta | null>;

/**
 * Fetches the document metadata for all twenty-three document slots of a given
 * `employees` row.
 *
 * Calls `GET /employees/:id/documents`.
 * Requires `employees.read` permission.
 *
 * @param accountingId - The numeric id of the `employees` row.
 * @returns A map of doc_type → metadata (or `null` when the slot is empty).
 */
export const listEmployeeDocuments = (
    accountingId: number | string
): Promise<EmployeeDocumentsMap> =>
    customRequest(`employees/${accountingId}/documents`).then(
        ({ json }) => json as EmployeeDocumentsMap
    );

/**
 * Uploads (or replaces) a document for a given slot.
 *
 * Calls `POST /employees/:id/documents` with a `multipart/form-data`
 * body containing `doc_type` and the `file`.
 * Requires `employees.manage` permission.
 *
 * @param accountingId - The numeric id of the `employees` row.
 * @param docType      - The document slot identifier.
 * @param file         - The file to upload.
 * @returns The stored document metadata.
 */
export const uploadEmployeeDocument = async (
    accountingId: number | string,
    docType: DocType,
    file: File
): Promise<EmployeeDocumentMeta> => {
    const token = getJWT();
    const fd = new FormData();
    fd.append('doc_type', docType);
    fd.append('file', file);

    const response = await fetch(`${apiUrl}/employees/${accountingId}/documents`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
    });

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Upload failed');
    }

    return response.json() as Promise<EmployeeDocumentMeta>;
};

/**
 * Downloads the file for a given document slot by triggering a browser download.
 *
 * Calls `GET /employees/:id/documents/:docType` and instructs the
 * browser to download the response body with the `Content-Disposition` filename.
 * Requires `employees.read` permission.
 *
 * @param accountingId - The numeric id of the `employees` row.
 * @param docType      - The document slot identifier.
 */
export const downloadEmployeeDocument = async (
    accountingId: number | string,
    docType: DocType
): Promise<void> => {
    const token = getJWT();
    const response = await fetch(
        `${apiUrl}/employees/${accountingId}/documents/${docType}`,
        { headers: { Authorization: `Bearer ${token}` } }
    );

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Download failed');
    }

    const blob = await response.blob();
    // Extract filename from Content-Disposition header, fall back to docType.
    const cd = response.headers.get('Content-Disposition') ?? '';
    const nameMatch = cd.match(/filename="([^"]+)"/);
    const filename = nameMatch ? decodeURIComponent(nameMatch[1]) : `${docType}`;

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
};

/**
 * Deletes the document stored in a given slot.
 *
 * Calls `DELETE /employees/:id/documents/:docType`.
 * Requires `employees.manage` permission.
 *
 * @param accountingId - The numeric id of the `employees` row.
 * @param docType      - The document slot identifier.
 * @returns The API response `{ deleted: true }`.
 */
export const deleteEmployeeDocument = (
    accountingId: number | string,
    docType: DocType
): Promise<{ deleted: boolean }> =>
    customRequest(`employees/${accountingId}/documents/${docType}`, {
        method: 'DELETE',
    }).then(({ json }) => json as { deleted: boolean });

/**
 * The result of fetching a document blob for inline viewing.
 */
export interface EmployeeDocumentBlob {
    /**
     * A `blob:` object URL that can be used as `src` in an `<img>` or `<iframe>`.
     * The caller is responsible for revoking this URL when it is no longer needed
     * by calling `URL.revokeObjectURL(url)`.
     */
    url: string;
    /** MIME type of the document (e.g. `"image/jpeg"` or `"application/pdf"`). */
    mimeType: string;
    /** Original filename as reported by the `Content-Disposition` header. */
    filename: string;
}

/**
 * Fetches a document from R2 and returns a temporary object URL for inline viewing.
 *
 * Unlike {@link downloadEmployeeDocument}, this function does **not** trigger a
 * browser download. Instead it returns a `blob:` URL together with the MIME type
 * so the caller can render the file inline — e.g. as an `<img>` for images or an
 * `<iframe>` for PDFs.
 *
 * **Important:** The returned `url` is a `blob:` object URL created via
 * `URL.createObjectURL`. Call `URL.revokeObjectURL(url)` when the viewer is
 * closed to release memory.
 *
 * Calls `GET /employees/:id/documents/:docType`.
 * Requires `employees.read` permission.
 *
 * @param accountingId - The numeric id of the `employees` row.
 * @param docType      - The document slot identifier.
 * @returns An {@link EmployeeDocumentBlob} containing the object URL, MIME type,
 *          and original filename.
 * @throws {Error} When the server returns a non-OK status.
 *
 * @example
 * const { url, mimeType } = await fetchEmployeeDocumentBlob(42, 'id_card');
 * // Use `url` as <img src={url} /> or <iframe src={url} />
 * // When done: URL.revokeObjectURL(url);
 */
export const fetchEmployeeDocumentBlob = async (
    accountingId: number | string,
    docType: DocType
): Promise<EmployeeDocumentBlob> => {
    const token = getJWT();
    const response = await fetch(
        `${apiUrl}/employees/${accountingId}/documents/${docType}`,
        { headers: { Authorization: `Bearer ${token}` } }
    );

    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Failed to load document');
    }

    const mimeType = response.headers.get('Content-Type') ?? 'application/octet-stream';
    const blob = await response.blob();

    const cd = response.headers.get('Content-Disposition') ?? '';
    const nameMatch = cd.match(/filename="([^"]+)"/);
    const filename = nameMatch ? decodeURIComponent(nameMatch[1]) : `${docType}`;

    const url = URL.createObjectURL(blob);
    return { url, mimeType, filename };
};

// ---------------------------------------------------------------------------
// Accounting Timesheet Export
// ---------------------------------------------------------------------------

/**
 * Triggers a browser download of the accounting timesheet XLSX file for the
 * specified year (or the current year when `year` is omitted).
 *
 * Makes a `GET /employees/export-timesheet` request (with an optional
 * `?year=<year>` query parameter) and instructs the browser to download the
 * response body as `timesheet_<year>.xlsx`.
 *
 * Year-selection rules (enforced server-side):
 * - Outside January 1–6: only the current calendar year is valid.
 * - Between January 1 and January 6 inclusive: either the current or the
 *   previous year may be requested.
 *
 * @param year - Optional 4-digit year override. Defaults to the current year.
 * @throws {Error} When the server returns a non-OK status (e.g. 400 Bad Request
 *   for an invalid year, or 403 Forbidden for missing `employees.export` permission).
 *
 * @example
 * // Export the current year's timesheet
 * await exportTimesheet();
 *
 * @example
 * // Export a specific year (only valid within the Jan 1–6 window for prev year)
 * await exportTimesheet(2025);
 */
export const exportTimesheet = async (year?: number): Promise<void> => {
    const token = getJWT();
    const query = year !== undefined ? `?year=${encodeURIComponent(year)}` : '';
    const url = `${apiUrl}/employees/export-timesheet${query}`;
    const response = await fetch(url, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
    if (!response.ok) {
        const text = await response.text();
        throw new Error(text || 'Timesheet export failed');
    }
    const blob = await response.blob();
    const targetYear = year ?? new Date().getFullYear();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = `timesheet_${targetYear}.xlsx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
};

// ---------------------------------------------------------------------------
// Termination workflow
// ---------------------------------------------------------------------------

/**
 * Valid task types for a termination checklist item. Mirrors the backend's
 * `TerminationTaskType` in `websites/backend/hr-system/types.d.ts`.
 */
export type TerminationTaskType = 'account_deletion' | 'manager_handoff' | 'hr_paperwork' | 'social_insurance_closing';

/**
 * Status values for a termination checklist item. `'processing'` is only
 * ever used by the `social_insurance_closing` task type.
 */
export type TerminationItemStatus = 'pending' | 'processing' | 'done';

/** The two proof-image slots a `social_insurance_closing` task can receive an upload for. */
export type TerminationProofSlot = 'processing' | 'done';

/** Public shape of a termination checklist item, as returned by list/get/summary endpoints. */
export interface TerminationChecklistItemDto {
    id: number;
    employee_id: string;
    task_type: TerminationTaskType;
    assignee_email: string;
    due_date: string;
    status: TerminationItemStatus;
    completed_by: string | null;
    completed_date: string | null;
    processing_by: string | null;
    processing_date: string | null;
    has_processing_proof: boolean;
    has_done_proof: boolean;
    created_date: string;
    updated_date: string;
}

/**
 * Response from `POST /employees/:id/terminate`.
 */
export interface TerminateEmployeeResponse {
    /** Updated employee accounting record. */
    employee_accounting: Record<string, unknown>;
    /** The four auto-created termination checklist items. */
    checklist: Array<{
        id: number;
        employee_id: string;
        task_type: TerminationTaskType;
        assignee_email: string;
        due_date: string;
        status: TerminationItemStatus;
        completed_by: string | null;
        completed_date: string | null;
    }>;
}

/**
 * Terminate an employee by calling `POST /employees/:id/terminate`.
 *
 * Sets the employee's `termination_date` and auto-creates the three-item
 * offboarding checklist.  Requires the `employees.manage` permission.
 *
 * @param id              - The numeric `id` of the `employee_accounting` row.
 * @param terminationDate - ISO 8601 date string (`YYYY-MM-DD`).
 * @returns The updated employee accounting record and the created checklist items.
 * @throws {Error} When the server returns a non-OK status.
 *
 * @example
 * const result = await terminateEmployee(42, '2026-06-01');
 * console.log(result.checklist.length); // 3
 */
export const terminateEmployee = async (
    id: number | string,
    terminationDate: string
): Promise<TerminateEmployeeResponse> => {
    const token = getJWT();
    const response = await fetch(`${apiUrl}/employees/${id}/terminate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ termination_date: terminationDate }),
    });
    if (!response.ok) {
        let message = 'Termination failed';
        try {
            const json = await response.json() as { message?: string };
            message = json.message ?? message;
        } catch {
            // ignore parse error
        }
        throw new Error(message);
    }
    return response.json() as Promise<TerminateEmployeeResponse>;
};

/**
 * Confirm a termination checklist item by calling
 * `PUT /terminations/:id/confirm`.
 *
 * Requires the `terminations.manage` permission.
 *
 * @param id - The numeric `id` of the termination checklist item.
 * @returns The updated checklist item.
 * @throws {Error} When the server returns a non-OK status.
 */
export const confirmTerminationItem = async (id: number | string): Promise<Record<string, unknown>> => {
    const token = getJWT();
    const response = await fetch(`${apiUrl}/terminations/${id}/confirm`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
        throw new Error('Confirmation failed');
    }
    return response.json() as Promise<Record<string, unknown>>;
};

/**
 * Uploads a proof image and advances a `social_insurance_closing` termination
 * task to `targetStatus` ('processing' or 'done').
 *
 * Calls `POST /terminations/:id/proof` with a `multipart/form-data` body
 * containing `target_status` and `file`. Requires the `terminations.manage`
 * permission. Only the `social_insurance_closing` task type accepts this
 * endpoint — other task types must use {@link confirmTerminationItem}.
 *
 * @param id           - The numeric `id` of the termination checklist item.
 * @param targetStatus - The status transition being proven.
 * @param file         - The proof image (jpeg/png/webp).
 * @returns The updated checklist item (public DTO shape).
 * @throws {Error} When the server returns a non-OK status.
 */
export const uploadTerminationProof = async (
    id: number | string,
    targetStatus: TerminationProofSlot,
    file: File
): Promise<TerminationChecklistItemDto> => {
    const token = getJWT();
    const fd = new FormData();
    fd.append('target_status', targetStatus);
    fd.append('file', file);

    const response = await fetch(`${apiUrl}/terminations/${id}/proof`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
    });

    if (!response.ok) {
        let message = 'Proof upload failed';
        try {
            const json = await response.json() as { message?: string };
            message = json.message ?? message;
        } catch {
            // ignore parse error
        }
        throw new Error(message);
    }

    return response.json() as Promise<TerminationChecklistItemDto>;
};

/**
 * Fetches a termination task's proof image from R2 and returns a temporary
 * object URL for inline preview.
 *
 * Calls `GET /terminations/:id/proof/:slot`. Requires the `terminations.read`
 * permission. Caller must call `URL.revokeObjectURL(url)` when done.
 *
 * @param id   - The numeric `id` of the termination checklist item.
 * @param slot - Which proof image to fetch ('processing' or 'done').
 * @returns A `blob:` object URL, or `null` when no proof has been uploaded for that slot.
 */
export const fetchTerminationProofBlob = async (
    id: number | string,
    slot: TerminationProofSlot
): Promise<string | null> => {
    const token = getJWT();
    const response = await fetch(`${apiUrl}/terminations/${id}/proof/${slot}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 404) return null;
    if (!response.ok) {
        throw new Error('Failed to load proof image');
    }
    const blob = await response.blob();
    return URL.createObjectURL(blob);
};

// ---------------------------------------------------------------------------
// Timesheets (attendance) module
// ---------------------------------------------------------------------------

/**
 * The eight fixed attendance codes from the "Bảng chấm công" daily timesheet
 * template. Kept in sync by hand with the backend `TimesheetCode` union in
 * `websites/backend/hr-system/types.d.ts`.
 */
export type TimesheetCode = 'D' | 'D/2' | 'P' | 'P/2' | 'KL' | 'NB' | 'NT' | 'KP';

/** All valid {@link TimesheetCode} values, for rendering pickers/legends. */
export const TIMESHEET_CODES: readonly TimesheetCode[] = ['D', 'D/2', 'P', 'P/2', 'KL', 'NB', 'NT', 'KP'];

/** A single day's attendance record for one employee. */
export interface TimesheetEntry {
    id?: number;
    employee_id: string;
    /** ISO 8601 date string (YYYY-MM-DD). */
    work_date: string;
    code: TimesheetCode;
    note?: string | null;
    /**
     * `true` when this entry was generated from the employee's work
     * schedule/approved timeoff rather than explicitly saved by HR. Only
     * present on `GET /timesheets` responses — never sent on `PUT` and never
     * present on the rows `saveTimesheets` returns, since a save always
     * persists an explicit HR value.
     */
    is_default?: boolean;
}

/** Response shape for `GET /timesheets`. */
export interface TimesheetsResponse {
    mode: 'week' | 'month';
    /** ISO 8601 date string (YYYY-MM-DD) — first day of the resolved range. */
    start: string;
    /** ISO 8601 date string (YYYY-MM-DD) — last day of the resolved range. */
    end: string;
    employees: Employee[];
    entries: TimesheetEntry[];
    /** Every `YYYY-MM-DD` date in `[start, end]` that is a public holiday, for column coloring. */
    holidays: string[];
}

/**
 * Fetches the week/month timesheet grid data: all active employees plus
 * their attendance entries for the resolved range.
 *
 * @param mode - `'week'` or `'month'`.
 * @param date - Any date (YYYY-MM-DD) within the desired range.
 * @returns The resolved range, active employees, and their timesheet entries.
 */
export const getTimesheets = (mode: 'week' | 'month', date: string): Promise<TimesheetsResponse> =>
    customRequest(`timesheets?mode=${mode}&date=${encodeURIComponent(date)}`).then(
        ({ json }) => json as TimesheetsResponse
    );

/** A single attendance cell edit to persist via {@link saveTimesheets}. */
export interface SaveTimesheetEntryInput {
    employeeId: string;
    /** ISO 8601 date string (YYYY-MM-DD). */
    workDate: string;
    code: TimesheetCode;
}

/**
 * Upserts a batch of attendance entries via `PUT /timesheets`.
 *
 * Entries whose `workDate` is older than 7 days are rejected by the backend
 * with a 403 unless `confirmation` is the exact string `"confirm"`.
 *
 * @param entries      - The attendance entries to save.
 * @param confirmation - Pass `"confirm"` to override the 7-day-old guard.
 * @returns The saved `TimesheetEntry` rows.
 */
export const saveTimesheets = (
    entries: SaveTimesheetEntryInput[],
    confirmation?: string
): Promise<TimesheetEntry[]> =>
    customRequest('timesheets', {
        method: 'PUT',
        body: JSON.stringify(confirmation ? { entries, confirmation } : { entries }),
    }).then(({ json }) => json as TimesheetEntry[]);

/** A single attendance cell to reset (delete its HR-saved override) via {@link resetTimesheets}. */
export interface ResetTimesheetEntryInput {
    employeeId: string;
    /** ISO 8601 date string (YYYY-MM-DD). */
    workDate: string;
}

/**
 * Resets (deletes) a batch of HR-saved attendance overrides via `DELETE /timesheets`,
 * reverting those cells back to their generated default code. The inverse of
 * {@link saveTimesheets}.
 *
 * Entries whose `workDate` is older than 7 days are rejected by the backend
 * with a 403 unless `confirmation` is the exact string `"confirm"`, mirroring
 * {@link saveTimesheets}'s 7-day rule.
 *
 * @param entries      - The cells to reset.
 * @param confirmation - Pass `"confirm"` to override the 7-day-old guard.
 * @returns The recomputed default `TimesheetEntry` rows (each flagged `is_default: true`).
 */
export const resetTimesheets = (
    entries: ResetTimesheetEntryInput[],
    confirmation?: string
): Promise<TimesheetEntry[]> =>
    customRequest('timesheets', {
        method: 'DELETE',
        body: JSON.stringify(confirmation ? { entries, confirmation } : { entries }),
    }).then(({ json }) => json as TimesheetEntry[]);
